import React, { useState, useEffect } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { apiFetch } from '../lib/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback data in case API is empty or unavailable
const fallbackResults = [
  {
    metric: '+180%',
    title: 'Crescita fatturato',
    description: 'Azienda B2B servizi. Da €200K a €560K in 18 mesi.',
  },
  {
    metric: '€850K',
    title: 'Nuovo mercato',
    description: 'Lancio internazionale. ROI 4x sull\'investimento marketing.',
  },
  {
    metric: '-45%',
    title: 'Costo acquisizione',
    description: 'E-commerce fashion. CAC ridotto da €38 a €21.',
  },
];

const StorySection = () => {
  const [results, setResults] = useState(fallbackResults);
  const [titleRef, titleVisible] = useScrollAnimation();
  const [resultsRef, resultsVisible] = useScrollAnimation();
  const [philosophyRef, philosophyVisible] = useScrollAnimation();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/results`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setResults(data);
          }
        }
      } catch (error) {
        console.error('Error fetching results:', error);
      }
    };
    fetchResults();
  }, []);

  return (
    <section id="results" className="bg-[#0b114e] py-24 lg:py-32 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[#d4883a]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
        {/* Title */}
        <div 
          ref={titleRef}
          className={`text-center mb-20 animate-on-scroll ${titleVisible ? 'animated' : ''}`}
        >
          <p className="text-[#d4883a] font-semibold mb-4 tracking-wide">Risultati</p>
          <h2 
            className="text-4xl lg:text-6xl text-white leading-tight"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Non prometto.{' '}
            <span className="text-[#d4883a]">Dimostro.</span>
          </h2>
        </div>

        {/* Results Grid */}
        <div ref={resultsRef} className="grid md:grid-cols-3 gap-8 mb-20">
          {results.map((result, index) => (
            <div 
              key={result.id || index}
              className={`bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center animate-scale stagger-${index + 1} ${resultsVisible ? 'animated' : ''}`}
            >
              <p className="text-5xl lg:text-6xl font-bold text-[#d4883a] mb-4">{result.metric}</p>
              <h3 className="text-xl font-bold text-white mb-2">{result.title}</h3>
              <p className="text-white/60">{result.description || result.desc}</p>
            </div>
          ))}
        </div>

        {/* Philosophy */}
        <div ref={philosophyRef} className="max-w-5xl mx-auto mt-8">
          <div className={`text-center mb-16 animate-on-scroll ${philosophyVisible ? 'animated' : ''}`}>
            <h3 
              className="text-3xl lg:text-5xl text-white"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              La mia <span className="text-[#d4883a]">filosofia</span>
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                number: "01",
                title: "Investimento, non costo",
                quote: "Il marketing non è un costo. È l'investimento con il ROI più alto che puoi fare, se sai cosa stai facendo.",
              },
              {
                number: "02",
                title: "Niente scorciatoie",
                quote: "Non lavoro con aziende che cercano scorciatoie. Lavoro con chi è pronto a costruire qualcosa di duraturo.",
              },
              {
                number: "03",
                title: "Risultati misurabili",
                quote: "Il mio successo si misura solo in un modo: i risultati dei miei clienti.",
              },
            ].map((item, index) => (
              <div 
                key={index} 
                className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4883a]/30 transition-all group"
              >
                {/* Number */}
                <span className="text-7xl font-bold text-[#d4883a]/10 absolute top-4 right-4 group-hover:text-[#d4883a]/20 transition-colors">
                  {item.number}
                </span>
                
                {/* Icon line */}
                <div className="w-12 h-1 bg-[#d4883a] rounded-full mb-6" />
                
                {/* Title */}
                <h4 className="text-white font-bold text-lg mb-4">{item.title}</h4>
                
                {/* Quote */}
                <p 
                  className="text-white/60 leading-relaxed italic"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  "{item.quote}"
                </p>
              </div>
            ))}
          </div>

          {/* Signature */}
          <div className="mt-16 flex items-center justify-center gap-6">
            <div className="h-px w-16 bg-white/20" />
            <div className="text-center">
              <p className="text-white font-semibold">Nicola Scerni</p>
              <p className="text-[#d4883a] text-sm">Strategic Marketing Consultant</p>
            </div>
            <div className="h-px w-16 bg-white/20" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StorySection;
